/**
 * @fileoverview World Set V3 schema definitions for staged physical spaces with sensory detail.
 *
 * Sets describe environments as a production designer or director would stage them --
 * focusing on lighting, atmosphere, soundscape, and sensory detail rather than
 * geographic identity (which is the domain of Places).
 *
 * This file contains:
 * - Canonical field definitions for world sets (single source of truth)
 * - Dynamically generated Zod schemas with type-safe casting
 * - All set-related schemas (entity, snapshot, idea, context variants)
 *
 * Pattern follows the character domain (character-sub-entities-v2.ts) and world-event.ts.
 */

import { z } from 'zod';
import { SHARED_WORLD_ELEMENT_FIELDS, type WorldElementFieldDefinition } from './shared-fields';

// =============================================================================
// CANONICAL FIELD DEFINITIONS - SINGLE SOURCE OF TRUTH
// =============================================================================

/**
 * Set-specific field definitions.
 * Combined with shared fields, these define all attributes of a world set.
 * Framed through a cinematic/production design lens.
 */
const SET_SPECIFIC_FIELDS: WorldElementFieldDefinition[] = [
  {
    key: 'lighting',
    label: 'Lighting',
    schemaDescription: "Light sources and quality of illumination",
    initialStateDescription: "The lighting design of this space -- sources, quality, color temperature, shadows, and how light shapes the mood.",
    journeyDescription: "How the lighting has changed or been revealed differently in this chapter.",
  },
  {
    key: 'atmosphere',
    label: 'Atmosphere & Mood',
    schemaDescription: "The emotional tone and ambiance",
    initialStateDescription: "The emotional atmosphere and mood of this space -- what a character would feel upon entering.",
    journeyDescription: "How the atmosphere and emotional tone of this space has shifted in this chapter.",
  },
  {
    key: 'soundscape',
    label: 'Soundscape',
    schemaDescription: "Ambient sounds and audio environment",
    initialStateDescription: "The ambient sounds, silence, echoes, or audio texture that define this space.",
    journeyDescription: "How the soundscape has changed or what new sounds have been introduced.",
  },
  {
    key: 'smells',
    label: 'Smells & Scents',
    schemaDescription: "Odors and fragrances present",
    initialStateDescription: "The scents, odors, and fragrances that permeate this space.",
    journeyDescription: "How the olfactory landscape has changed or what new scents have emerged.",
  },
  {
    key: 'temperature',
    label: 'Temperature & Air',
    schemaDescription: "Warmth, humidity, air quality",
    initialStateDescription: "The temperature, humidity, air movement, and tactile quality of the environment.",
    journeyDescription: "How the temperature, air quality, or physical comfort of the space has changed.",
  },
  {
    key: 'keyObjects',
    label: 'Key Objects & Arrangement',
    schemaDescription: "Important items and their positioning",
    initialStateDescription: "The key objects, furnishings, and their arrangement -- what draws the eye and how the space is staged.",
    journeyDescription: "How the arrangement of objects has changed or what new items have appeared or been removed.",
  },
  {
    key: 'entrances',
    label: 'Entrances & Exits',
    schemaDescription: "Ways in and out of the space",
    initialStateDescription: "The ways in and out of this space -- doors, windows, passages, and their current state (open, locked, hidden).",
    journeyDescription: "How access points have changed -- blocked, revealed, or altered.",
  },
  {
    key: 'timeWeather',
    label: 'Time & Weather',
    schemaDescription: "Time of day and weather conditions (if applicable)",
    initialStateDescription: "The time of day, season, and weather conditions that affect this space (if applicable).",
    journeyDescription: "How the time of day or weather conditions have shifted and affected the environment.",
  },
];

/**
 * Complete field definitions for world sets.
 * Combines shared fields with set-specific fields.
 */
export const WORLD_SET_FIELD_DEFINITIONS: readonly WorldElementFieldDefinition[] = [
  ...SHARED_WORLD_ELEMENT_FIELDS,
  ...SET_SPECIFIC_FIELDS,
] as const;

// =============================================================================
// ENUMS
// =============================================================================

/**
 * Element subtypes for world sets.
 * - interior: Indoor staged spaces with sensory detail ("The war room after the assassination", "A dimly lit cabin with overturned chairs")
 * - exterior: Outdoor staged spaces with sensory detail ("The moonlit courtyard during the duel", "The busy marketplace at noon")
 */
export const WorldSetElementEnum = z.enum(['interior', 'exterior']);
export type WorldSetElement = z.infer<typeof WorldSetElementEnum>;

// =============================================================================
// DYNAMICALLY GENERATED SCHEMAS (following character domain pattern)
// =============================================================================

/**
 * Builds the base world set attributes schema from the canonical field definitions.
 * All fields are optional for world sets.
 */
const buildAttributesSchema = () => {
  const schemaShape = WORLD_SET_FIELD_DEFINITIONS.reduce((acc, field) => {
    acc[field.key] = z.string().optional().describe(field.schemaDescription);
    return acc;
  }, {} as Record<string, z.ZodOptional<z.ZodString>>);

  // Type cast to preserve TypeScript inference (must be kept in sync with field definitions)
  return z.object(schemaShape as {
    // Shared fields
    relevanceToPlot: z.ZodOptional<z.ZodString>;
    currentState: z.ZodOptional<z.ZodString>;
    knownInformation: z.ZodOptional<z.ZodString>;
    // Set-specific fields
    lighting: z.ZodOptional<z.ZodString>;
    atmosphere: z.ZodOptional<z.ZodString>;
    soundscape: z.ZodOptional<z.ZodString>;
    smells: z.ZodOptional<z.ZodString>;
    temperature: z.ZodOptional<z.ZodString>;
    keyObjects: z.ZodOptional<z.ZodString>;
    entrances: z.ZodOptional<z.ZodString>;
    timeWeather: z.ZodOptional<z.ZodString>;
  });
};

/**
 * The base schema for world set attributes, used by agents that generate or extract
 * set details. All attributes are optional.
 */
export const WorldSetAttributesV3Schema = buildAttributesSchema();

// =============================================================================
// ENTITY SCHEMAS
// =============================================================================

/**
 * A point-in-time snapshot of a world set's state at the end of a specific chapter.
 * Extends attributes with temporal metadata.
 */
export const WorldSetSnapshotV3Schema = WorldSetAttributesV3Schema.extend({
  id: z.string().describe("Unique identifier for this snapshot (typically matches chapterId)."),
  chapterId: z.string().describe("ID of the chapter this snapshot corresponds to."),
  chapterNumber: z.number().describe("The number of the chapter."),
  timestamp: z.string().describe("ISO timestamp of when this snapshot was created."),
  notPresentInChapter: z.boolean().optional().describe("Flag indicating this is a carry-forward snapshot."),
});

/**
 * The canonical World Set entity.
 * A container for metadata and a timeline of snapshots.
 */
export const WorldSetV3Schema = z.object({
  id: z.string().describe("Unique identifier for the world set."),
  name: z.string().describe("The name of the set."),
  type: z.literal('set').describe("Discriminator for world element type."),
  element: WorldSetElementEnum.describe("The subtype of set (interior or exterior)."),
  initialDescription: z.string().optional().describe("Brief description of the set before detailed generation."),
  snapshots: z.array(WorldSetSnapshotV3Schema).describe("Complete timeline of set state across chapters."),
  latestSnapshotId: z.string().optional().describe("Quick reference to the most recent snapshot."),
  notes: z.string().optional().describe("User notes about this set."),
  mainImageId: z.string().optional().describe("Reference to the main image for this set."),
});

// =============================================================================
// IDEATION SCHEMAS
// =============================================================================

/**
 * Schema for a world set idea before it's added to the project.
 * Used during AI ideation and extraction flows.
 */
export const WorldSetIdeaV3Schema = z.object({
  tempId: z.string().describe("Temporary ID for UI tracking before persistence."),
  name: z.string().describe("The proposed name of the set."),
  element: WorldSetElementEnum.describe("The proposed subtype (interior or exterior)."),
  initialDescription: z.string().describe("Brief description of the set concept."),
  source: z.enum(['ideation', 'extraction', 'manual']).describe("How this idea was generated."),
});

// =============================================================================
// CONTEXT SCHEMAS (for AI agents)
// =============================================================================

/**
 * Minimal reference schema for world sets.
 * Used when only ID and name are needed.
 */
export const WorldSetRefV3Schema = z.object({
  id: z.string(),
  name: z.string(),
  type: z.literal('set'),
});

/**
 * Basic context schema for world sets.
 * Used for high-level summaries in agent context.
 */
export const WorldSetBasicV3Schema = z.object({
  name: z.string(),
  type: z.literal('set'),
  element: WorldSetElementEnum,
  initialDescription: z.string().optional(),
});

/**
 * Rich context schema for world sets.
 * Includes the full entity plus the resolved latest snapshot.
 */
export const WorldSetContextV3Schema = WorldSetV3Schema.extend({
  latestSnapshot: WorldSetSnapshotV3Schema.optional(),
});

// =============================================================================
// TYPE INFERENCE (single source of truth from Zod schemas)
// =============================================================================

export type WorldSetAttributes = z.infer<typeof WorldSetAttributesV3Schema>;
export type WorldSetSnapshotV3 = z.infer<typeof WorldSetSnapshotV3Schema>;
export type WorldSetV3 = z.infer<typeof WorldSetV3Schema>;
export type WorldSetIdeaV3 = z.infer<typeof WorldSetIdeaV3Schema>;
export type WorldSetRefV3 = z.infer<typeof WorldSetRefV3Schema>;
export type WorldSetBasicV3 = z.infer<typeof WorldSetBasicV3Schema>;
export type WorldSetContextV3 = z.infer<typeof WorldSetContextV3Schema>;
